import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, RefreshCcw } from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { CrudSection } from "./CrudSection";
import { Panel } from "../pages/calculator/parts";

const fmtDate = (iso) => {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} · ${hh}:${mi}`;
  } catch {
    return iso;
  }
};

const ClicksPanel = () => {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(
        `${API}/admin/partners/clicks`,
        authHeaders()
      );
      setRows(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load clicks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totals = rows
    ? rows.reduce(
        (acc, r) => {
          acc.total += r.total || 0;
          acc.last7 += r.last7 || 0;
          acc.last30 += r.last30 || 0;
          return acc;
        },
        { total: 0, last7: 0, last30: 0 }
      )
    : null;

  return (
    <div className="bg-white border border-forest/10 p-6 sm:p-8" data-testid="partner-clicks-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="font-serif text-2xl text-forest tracking-tight">
            Partner clicks
          </h2>
          <p className="text-[12.5px] text-sage mt-1.5 leading-relaxed">
            Counts outbound clicks on each partner logo on the homepage. No visitor
            details are stored — just which partner was clicked and when. Dates
            shown in DD/MM/YYYY.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="partner-clicks-refresh"
          disabled={loading}
          className="inline-flex items-center gap-2 border border-forest/25 hover:border-forest text-forest px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors disabled:opacity-50"
        >
          <RefreshCcw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {err ? (
        <p className="text-sm text-red-700">{err}</p>
      ) : rows === null ? (
        <p className="text-sm text-sage">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-sage">No partners yet. Add one below.</p>
      ) : (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: "Total clicks", value: totals.total },
              { label: "Last 7 days", value: totals.last7 },
              { label: "Last 30 days", value: totals.last30 },
            ].map((t) => (
              <div key={t.label} className="bg-cream-3 border border-forest/10 px-4 py-3">
                <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-forest/70">
                  {t.label}
                </p>
                <p className="font-serif text-2xl text-forest mt-0.5 tabular-nums">
                  {t.value}
                </p>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full text-[13px]">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.14em] uppercase font-bold text-forest/70 border-b border-forest/15">
                  <th className="py-3 pr-3">Partner</th>
                  <th className="py-3 pr-3">Website</th>
                  <th className="py-3 pr-3 text-right">Total</th>
                  <th className="py-3 pr-3 text-right">Last 7 days</th>
                  <th className="py-3 pr-3 text-right">Last 30 days</th>
                  <th className="py-3 text-right">Last clicked</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.partner_id}
                    data-testid={`partner-clicks-row-${r.partner_id}`}
                    className="border-b border-forest/8"
                  >
                    <td className="py-3 pr-3 font-serif text-[15px] text-forest">
                      {r.name}
                    </td>
                    <td className="py-3 pr-3 text-sage break-all max-w-[320px]">
                      {r.website_url ? (
                        <a
                          href={r.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-forest hover:text-gold-dark transition-colors"
                        >
                          {r.website_url}
                          <ExternalLink size={11} />
                        </a>
                      ) : (
                        <span className="text-sage/60">No link set</span>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums font-semibold text-forest">
                      {r.total}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums">{r.last7}</td>
                    <td className="py-3 pr-3 text-right tabular-nums">{r.last30}</td>
                    <td className="py-3 text-right text-sage tabular-nums whitespace-nowrap">
                      {fmtDate(r.last_clicked_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <ul className="sm:hidden divide-y divide-forest/10">
            {rows.map((r) => (
              <li
                key={r.partner_id}
                data-testid={`partner-clicks-card-${r.partner_id}`}
                className="py-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-serif text-[16px] text-forest truncate">
                      {r.name}
                    </p>
                    <p className="text-[11px] text-sage break-all">
                      {r.website_url || "No link set"}
                    </p>
                  </div>
                  <p className="font-serif text-xl text-forest tabular-nums">
                    {r.total}
                  </p>
                </div>
                <div className="flex gap-4 mt-2 text-[11.5px] text-sage">
                  <span>7d: <strong className="text-forest">{r.last7}</strong></span>
                  <span>30d: <strong className="text-forest">{r.last30}</strong></span>
                  <span className="ml-auto">{fmtDate(r.last_clicked_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export const PartnersSection = ({ fields, emptyItem, itemTitle, itemSub, itemThumb }) => (
  <div className="space-y-6">
    <ClicksPanel />
    <CrudSection
      title="Partners"
      subtitle="Manage the logos displayed in the Trusted Partners strip on the homepage."
      collection="partners"
      testidPrefix="partners"
      fields={fields}
      emptyItem={emptyItem}
      itemTitle={itemTitle}
      itemSub={itemSub}
      itemThumb={itemThumb}
    />
  </div>
);
